import { BASE_GRID_OPTIONS, PoGrid } from '@/components/grid/PoGrid';
import { FunctionComponent } from 'react';
import { GridApi, GridOptions, ICellRendererParams } from 'ag-grid-community';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import { useTranslation } from '@/lib';
import LinkCellRenderer, { LinkCellRendererParams } from '@/components/grid/cells/LinkCellRenderer';

export interface BuildupDataModel {
  id: number | null;
  counterpartLocationName: string;
  counterpartLocationCode: string;
  balance: number;
}

interface BuildupGridProps {
  data?: BuildupDataModel[];
  isError: boolean;
  noBorder?: boolean;
  isLoading?: boolean;
  onExportToExcelClicked: (gridApi: GridApi<BuildupDataModel> | undefined) => void;
  isGuestView?: boolean;
}

export const BuildupGrid: FunctionComponent<BuildupGridProps> = ({
  data,
  isError,
  noBorder,
  isLoading,
  onExportToExcelClicked,
  isGuestView = false,
}) => {
  const { t } = useTranslation();

  const customGridOptions: GridOptions<BuildupDataModel> = {
    ...BASE_GRID_OPTIONS,
    getContextMenuItems: (params) => [
      'copy',
      'separator',
      {
        name: t('export_excel'),
        action: () => onExportToExcelClicked(params.api),
      },
    ],
  };

  const columns: ColDefOrGroup<BuildupDataModel>[] = [
    {
      field: 'counterpartLocationName',
      headerName: t('counterpart_location'),
      flex: 2,
      ...(isGuestView
        ? {}
        : {
            cellRenderer: LinkCellRenderer,
            cellRendererParams: (params: ICellRendererParams<BuildupDataModel>): LinkCellRendererParams => ({
              pathname: params.data?.id ? `/app/locations/${params.data.id}/balance` : undefined,
              target: '_blank',
            }),
          }),
      valueGetter: (params) => {
        if (!params.data) return '';
        return `${params.data.counterpartLocationName}`;
      },
    },
    {
      colId: 'debit',
      headerName: t('debit'),
      flex: 1,
      valueGetter: (params) => {
        if (!params.data) return '';
        return params.data.balance < 0 ? Math.abs(params.data.balance) : '';
      },
    },
    {
      colId: 'credit',
      headerName: t('credit'),
      flex: 1,
      valueGetter: (params) => {
        if (!params.data) return '';
        return params.data.balance > 0 ? params.data.balance : '';
      },
    },
  ];

  return (
    <div className="flex h-full flex-1 grow flex-col">
      {isError ? (
        <ErrorLoadingDataAlert />
      ) : (
        <PoGrid
          noBorder={noBorder}
          isLoading={isLoading}
          colDefs={columns}
          rowData={data}
          gridOptions={customGridOptions}
          disableDefaultGridOptions
        />
      )}
    </div>
  );
};
